import { usePageActions, PageContext } from "../../PageContent";
import { BlockButton, Breakpoints, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, SpinnerDefault, TitlelessCard } from "../../../components";
import { useCallback, useRef, useState } from "react";
import { ODataResponse, LevelModel } from "../../../data/entities";
import { LevelService } from "../../../services";
import { useOutletContext } from "react-router-dom";
import { AddLevelModal, EditLevelModal } from "./components";
import { useSharedState } from "../../../app/sharedProperty";
import { showSuccessMessage, showErrorMessage } from "../../../app/tools";
import { MenuItem, MenuSelectEvent } from "@progress/kendo-react-layout";
import { GridCellProps } from "@progress/kendo-react-grid";

export function SettingsLevelsCard() {

  const context: PageContext = useOutletContext();
  context.setTitle("Settings: Levels");
  
  const gridData = useSharedState<Array<LevelModel>>([]);
  const gridRef = useRef<ODataGrid>(null);
  const showAddState = useSharedState(false);
  const showEditState = useSharedState(false);
  const showDeleteState = useSharedState(false);
  const editItemState = useSharedState<LevelModel>({} as LevelModel)
  const deleteItemState = useSharedState<LevelModel>({} as LevelModel)
  const [showSpinner, setShowSpinner] = useState(false);

  const levelService = new LevelService();

  usePageActions(
    <BlockButton onClick={() => { showAddState.setter(true); }}>
      <IconAndText iconName={IconDefinitions.add} text="Add New Level" />
    </BlockButton>
  );

  const getLevels = (oDataQuery: string): Promise<ODataResponse<any>> => 
    levelService.getLevelsAsync(oDataQuery);

  const onRowMenuSelect = (e: MenuSelectEvent, dataItem: any) => {
    if (dataItem.isDeleted) return;
    switch (e.item.data.action){
      case "edit":
        showEdit(dataItem);
        break;
      case "delete":
        showDelete(dataItem);
    }
  }

  const showEdit = (dataItem: LevelModel) => {
    editItemState.setter(dataItem);
    showEditState.setter(true);
  }

  const showDelete = (dataItem: LevelModel) => {
    deleteItemState.setter(dataItem);
    showDeleteState.setter(true);
  }

  const addLevel = async (level: LevelModel) => {
    try {
      showAddState.setter(false);
      setShowSpinner(true);
      const response = await levelService.addLevelAsync(level);
      gridRef.current?.refreshData();
      if (response){
        showSuccessMessage("Level added successfully.");
      } else {
        showErrorMessage("Unable to add new level.");
      }
    } catch (err) {
      throw new Error(`Unable to add new level, ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const editLevel = async (level: LevelModel) => {
    setShowSpinner(true);
    try{
      showEditState.setter(false);
      await levelService.editLevelAsync(level);
      gridRef.current?.refreshData();
      showSuccessMessage("Level updated successfully.");
    } catch (err) {
      throw new Error(`Unable to edit level: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const deleteLevel = async (level: LevelModel) => {
    setShowSpinner(true);
    try {
      await levelService.deleteLevelByIdAsync(level.id);
      gridRef.current?.refreshData();
      showSuccessMessage("Level deleted successfully.");
    } catch(err) {
      throw new Error(`Unable to delete level: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <>
      <SpinnerDefault show={showSpinner} fullscreen={true} />
      <TitlelessCard bodyClassName="p-0">
        <ODataGrid
          ref={gridRef}
          fullHeight={true}
          getData={getLevels}
          dataState={gridData}
          onRowMenuSelect={onRowMenuSelect}
          onRowClick={(e) => { showEdit(e.dataItem); }}
          sort={[{ field: "name", dir: "desc"}]}
          scrollable="scrollable"
          pageSize={100}
        >
          <ResponsiveGridColumn
            title="Name"
            field="name"
            sortable={true}
          />
          <ResponsiveGridColumn
            title="Code"
            field="code"
            sortable={true}
          />
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={commandsCell}
            width={50}
            breakpoint={Breakpoints.sm}
          />
          <MenuItem
            text="Edit"
            data={{ action: "edit" }}
            icon="hyperlink-open"
          />
          <MenuItem
            text="Delete"
            data={{ action: "delete" }}
            icon={IconDefinitions.delete}
          />
        </ODataGrid>
      </TitlelessCard>
      <AddLevelModal
        showState={showAddState}
        onSubmit={addLevel}
      />
      <EditLevelModal
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={editLevel}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete Level ${deleteItemState.value?.name}`}
        message={`You are about to delete this level, it cannot be undone. Are you sure you want to delete level ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteLevel(deleteItemState.value)}
      />
    </>
  );
}

export default SettingsLevelsCard;

